import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import packageService from '../services/packageService';
import { Package } from '../types';

interface ViewPackageDialogProps {
  open: boolean;
  onClose: () => void;
  packageId: string | null;
  onEdit?: (pkg: Package) => void;
  canEdit?: boolean;
}

const ViewPackageDialog: React.FC<ViewPackageDialogProps> = ({
  open,
  onClose,
  packageId,
  onEdit,
  canEdit = false,
}) => {
  const { data: packageData, isLoading, error } = useQuery({
    queryKey: ['package-detail', packageId],
    queryFn: () => packageService.getPackage(packageId!),
    enabled: open && !!packageId,
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return format(date, 'dd/MM/yyyy HH:mm');
    } catch {
      return '-';
    }
  };

  const formatCurrency = (amount?: number | string) => {
    const num = Number(amount ?? 0);
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(Number.isFinite(num) ? num : 0);
  };

  const formatQuota = (bytes?: number | string | null) => {
    if (!bytes || Number(bytes) === 0) return 'Unlimited';
    const n = Number(bytes);
    const gb = n / (1024 * 1024 * 1024);
    if (gb >= 1) return `${gb.toFixed(1)} GB`;
    const mb = n / (1024 * 1024);
    return `${mb.toFixed(0)} MB`;
  };

  const getStatusLabel = (status?: string) =>
    status === 'active' ? 'Aktif' : 'Nonaktif';

  const InfoItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box mb={2}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Typography variant="body1" fontWeight="medium" component="div">
        {value}
      </Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="h6">Detail Paket Internet</Typography>
            {packageData && (
              <Typography variant="body2" color="text.secondary">
                {packageData.name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">
            Gagal memuat detail paket:{' '}
            {(error as any)?.response?.data?.message || (error as Error).message}
          </Alert>
        )}

        {!isLoading && !error && packageData && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                Informasi Dasar
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" flexWrap="wrap" gap={3}>
                <Box flex="1" minWidth="220px">
                  <InfoItem label="Nama Paket" value={packageData.name} />
                  <InfoItem
                    label="Profile PPP"
                    value={
                      <Typography fontFamily="monospace" fontSize="0.9rem">
                        {packageData.profile_name}
                      </Typography>
                    }
                  />
                </Box>
                <Box flex="1" minWidth="220px">
                  <InfoItem
                    label="Status"
                    value={
                      <Chip
                        label={getStatusLabel(packageData.status)}
                        color={packageData.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    }
                  />
                  <InfoItem
                    label="SPOP"
                    value={
                      packageData.spop
                        ? `${packageData.spop.name}${packageData.spop.areaCode ? ` (${packageData.spop.areaCode})` : ''}`
                        : 'Semua SPOP'
                    }
                  />
                </Box>
              </Box>
              <InfoItem
                label="Deskripsi"
                value={packageData.description || 'Tidak ada deskripsi'}
              />
            </Box>

            <Box>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                Bandwidth & Prioritas
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" flexWrap="wrap" gap={3}>
                <Box flex="1" minWidth="180px">
                  <InfoItem
                    label="Upload"
                    value={packageData.bandwidth_upload ? `↑ ${packageData.bandwidth_upload}` : '-'}
                  />
                </Box>
                <Box flex="1" minWidth="180px">
                  <InfoItem
                    label="Download"
                    value={packageData.bandwidth_download ? `↓ ${packageData.bandwidth_download}` : '-'}
                  />
                </Box>
                <Box flex="1" minWidth="180px">
                  <InfoItem
                    label="Prioritas"
                    value={`${packageData.priority ?? 8}${
                      packageData.priority === 1
                        ? ' (Tertinggi)'
                        : packageData.priority === 8
                          ? ' (Terendah)'
                          : ''
                    }`}
                  />
                </Box>
              </Box>
              {(packageData.burst_limit || packageData.burst_threshold || packageData.burst_time) && (
                <Box mt={1}>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Burst
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={2}>
                    {packageData.burst_limit && (
                      <Chip size="small" label={`Limit: ${packageData.burst_limit}`} variant="outlined" />
                    )}
                    {packageData.burst_threshold && (
                      <Chip size="small" label={`Threshold: ${packageData.burst_threshold}`} variant="outlined" />
                    )}
                    {packageData.burst_time && (
                      <Chip size="small" label={`Time: ${packageData.burst_time}`} variant="outlined" />
                    )}
                  </Box>
                </Box>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                Harga & Masa Aktif
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" flexWrap="wrap" gap={3}>
                <Box flex="1" minWidth="180px">
                  <InfoItem
                    label="Harga"
                    value={
                      <Typography variant="h6" color="primary">
                        {formatCurrency(packageData.price)}
                      </Typography>
                    }
                  />
                </Box>
                <Box flex="1" minWidth="180px">
                  <InfoItem label="Masa Berlaku" value={`${packageData.validity_days ?? 30} hari`} />
                </Box>
                <Box flex="1" minWidth="180px">
                  <InfoItem label="Kuota Data" value={formatQuota(packageData.quota_bytes)} />
                </Box>
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                Riwayat
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" flexWrap="wrap" gap={3}>
                <Box flex="1" minWidth="180px">
                  <InfoItem label="Dibuat" value={formatDate(packageData.created_at)} />
                </Box>
                <Box flex="1" minWidth="180px">
                  <InfoItem label="Diperbarui" value={formatDate(packageData.updated_at)} />
                </Box>
              </Box>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {canEdit && packageData && onEdit && (
          <Button startIcon={<EditIcon />} onClick={() => onEdit(packageData)}>
            Edit
          </Button>
        )}
        <Button onClick={onClose} variant="contained">
          Tutup
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewPackageDialog;
